// Contratos PJ (substitui o Apps Script "Sistema Contratos PJ"): valor vigente de cada PJ, histórico,
// reajustes individuais e em lote, troca de CNPJ e aditivos gerados no Google Docs.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, FileUp, Search } from "lucide-react";
import { getRhAditivosPendentes, getRhContratosPj, getRhStatusDocumentos } from "../../api";
import { formatData } from "../estoque/comum";
import { AvisoErro, BadgeAtivo, btnStyle, inputStyle, palette, useIsMobile } from "../estoque/ui";
import { useFormatValor } from "./valores";
import AditivosPendentes from "./AditivosPendentes";
import ContratoPjModal from "./ContratoPjModal";
import LoteReajusteModal from "./LoteReajusteModal";
import RhLayout from "./RhLayout";

const semAcento = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

function pendencias(c) {
  const lista = [];
  if (!c.tem_qualificacao) lista.push("sem qualificação PJ");
  if (!c.empresa) lista.push("sem empresa");
  if (!c.qtd_movimentacoes) lista.push("sem histórico");
  return lista;
}

function AvisoDocumentos({ C, status }) {
  const [aberto, setAberto] = useState(false);
  if (!status || status.disponivel) return null;
  const problemas = Object.entries(status.itens).filter(([, erro]) => erro);
  return (
    <div style={{ background: `${C.warning}18`, border: `1px solid ${C.warning}`, borderRadius: 8, padding: "10px 12px", fontSize: 13, marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <AlertTriangle size={16} color={C.warning} style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ flex: 1 }}>
          <strong>Geração de aditivos indisponível.</strong> A conta de serviço da plataforma ainda não acessa {problemas.length} modelo(s)/pasta(s) do Drive.
          Reajustes e trocas de CNPJ podem ser registrados agora e os aditivos gerados depois.
          <button onClick={() => setAberto((v) => !v)} style={{ background: "none", border: "none", color: C.brand, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, marginLeft: 4 }}>
            {aberto ? "Ocultar detalhes" : "Ver detalhes"}
          </button>
          {aberto && <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{problemas.map(([item, msg]) => <li key={item}><strong>{item}:</strong> {msg}</li>)}</ul>}
        </div>
      </div>
    </div>
  );
}

export default function ContratosPjView() {
  const formatBRL = useFormatValor();
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const [lista, setLista] = useState(null);
  const [statusDocs, setStatusDocs] = useState(null);
  const [pendentes, setPendentes] = useState([]);
  const [erro, setErro] = useState("");
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState("Ativo");
  const [empresa, setEmpresa] = useState("");
  const [soPendentes, setSoPendentes] = useState(false);
  const [lote, setLote] = useState(false);
  const contratoAberto = params.get("contrato") ? Number(params.get("contrato")) : null;

  const carregar = useCallback(() => {
    getRhContratosPj().then(setLista).catch((e) => setErro(e.message));
    getRhAditivosPendentes().then(setPendentes).catch(() => setPendentes([]));
  }, []);
  useEffect(() => {
    carregar();
    getRhStatusDocumentos().then(setStatusDocs).catch(() => setStatusDocs({ disponivel: false, itens: {} }));
  }, [carregar]);

  const empresas = useMemo(() => Array.from(new Set((lista || []).map((c) => c.empresa).filter(Boolean))).sort(), [lista]);
  const filtrados = useMemo(() => {
    const termo = semAcento(busca.trim());
    const termoDigitos = busca.replace(/\D/g, "");
    return (lista || []).filter((c) =>
      (!termo || semAcento(`${c.nome} ${c.razao_social || ""}`).includes(termo) || (termoDigitos.length >= 3 && (c.cnpj || "").replace(/\D/g, "").includes(termoDigitos))) &&
      (!status || c.status === status) &&
      (!empresa || c.empresa === empresa) &&
      (!soPendentes || pendencias(c).length > 0)
    );
  }, [lista, busca, status, empresa, soPendentes]);
  const folha = filtrados.reduce((s, c) => s + Number(c.valor_atual || 0), 0);
  const qtdPendentes = (lista || []).filter((c) => c.status === "Ativo" && pendencias(c).length).length;

  const abrir = (id) => setParams(id ? { contrato: String(id) } : {});
  const documentosDisponiveis = Boolean(statusDocs?.disponivel);
  const th = (align = "left") => ({ textAlign: align, padding: "10px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: C.muted, borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.card });
  const td = (align = "left") => ({ textAlign: align, padding: "10px 12px", fontSize: 13, borderBottom: `1px solid ${C.border}` });

  return (
    <RhLayout titulo="Contratos PJ" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <AvisoDocumentos C={C} status={statusDocs} />
      {statusDocs?.disponivel && <AditivosPendentes C={C} pendentes={pendentes} onAlterado={carregar} />}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <div style={{ position: "relative", flex: "1 1 220px" }}>
          <Search size={15} style={{ position: "absolute", left: 12, top: 14, color: C.muted }} />
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar por nome, razão social ou CNPJ" style={{ ...inputStyle(C), paddingLeft: 34 }} />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ ...inputStyle(C), flex: "0 1 150px", width: "auto" }}>
          <option value="">Todos os status</option><option value="Ativo">Ativo</option><option value="Inativo">Inativo</option>
        </select>
        <select value={empresa} onChange={(e) => setEmpresa(e.target.value)} style={{ ...inputStyle(C), flex: "0 1 160px", width: "auto" }}>
          <option value="">Todas as empresas</option>
          {empresas.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
          <input type="checkbox" checked={soPendentes} onChange={(e) => setSoPendentes(e.target.checked)} /> Só com pendência ({qtdPendentes})
        </label>
        <button onClick={() => setLote(true)} style={{ ...btnStyle(C.brand), marginLeft: "auto" }}><FileUp size={15} /> Reajuste em lote</button>
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
        <strong style={{ color: C.text }}>{filtrados.length} contrato(s)</strong> · valor mensal vigente {formatBRL(folha)}
      </div>

      {lista === null && !erro && <div style={{ color: C.muted, fontSize: 13 }}>Carregando…</div>}
      {lista !== null && (isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtrados.map((c) => (
            <button key={c.contrato_id} onClick={() => abrir(c.contrato_id)} style={{ textAlign: "left", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, color: C.text, fontFamily: "inherit", cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <strong style={{ fontSize: 14 }}>{c.nome}</strong>
                <span style={{ fontWeight: 800 }}>{formatBRL(c.valor_atual)}</span>
              </div>
              <div style={{ fontSize: 12, color: C.muted }}>{[c.empresa, c.cnpj, c.ultimo_aditivo && `${c.ultimo_aditivo}º aditivo`].filter(Boolean).join(" · ")}</div>
              {pendencias(c).length > 0 && <div style={{ fontSize: 12, color: C.warning, marginTop: 4 }}>{pendencias(c).join(" · ")}</div>}
            </button>
          ))}
        </div>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "auto", maxHeight: "68vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th()}>PJ</th><th style={th()}>Empresa</th><th style={th()}>CNPJ</th>
                <th style={th("right")}>Valor vigente</th><th style={th()}>Desde</th><th style={th()}>Próximo reajuste</th>
                <th style={th("center")}>Último aditivo</th><th style={th()}>Pendências</th><th style={th("center")}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.length === 0 && <tr><td colSpan={9} style={{ ...td("center"), color: C.muted, padding: 24 }}>Nenhum contrato.</td></tr>}
              {filtrados.map((c) => (
                <tr key={c.contrato_id} onClick={() => abrir(c.contrato_id)} style={{ cursor: "pointer" }}>
                  <td style={{ ...td(), fontWeight: 600 }}>{c.nome}{c.razao_social && <div style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>{c.razao_social}</div>}</td>
                  <td style={td()}>{c.empresa || "—"}</td>
                  <td style={{ ...td(), whiteSpace: "nowrap" }}>{c.cnpj || "—"}</td>
                  <td style={{ ...td("right"), fontWeight: 700 }}>{formatBRL(c.valor_atual)}</td>
                  <td style={td()}>{c.vigente_desde ? formatData(c.vigente_desde) : "—"}</td>
                  <td style={td()}>{c.proximo_reajuste_em ? `${formatData(c.proximo_reajuste_em)} · ${formatBRL(c.proximo_valor)}` : "—"}</td>
                  <td style={td("center")}>{c.ultimo_aditivo ?? "—"}</td>
                  <td style={{ ...td(), fontSize: 12, color: C.warning }}>{pendencias(c).join(", ") || ""}</td>
                  <td style={td("center")}><BadgeAtivo C={C} ativo={c.status === "Ativo"} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {contratoAberto && (
        <ContratoPjModal C={C} contratoId={contratoAberto} documentosDisponiveis={documentosDisponiveis} onClose={() => abrir(null)} onAlterado={carregar} />
      )}
      {lote && <LoteReajusteModal C={C} documentosDisponiveis={documentosDisponiveis} onClose={() => setLote(false)} onAlterado={carregar} />}
    </RhLayout>
  );
}
